// src/components/RoomChargePanel.tsx (2026-09-26 新設)
// 個室料: 2026-08 の料金改定と、個室(差額室)の在室日の徴収状況。金額はすべて税込。
//   源 = fact_room_charge_monthly / dim_room_charge_room / dim_room_charge_meta (build_room_charge.R)。
//   区分の定義は build_room_charge.R 冒頭が正本。2026-09-28 から確定月だけ（速報は読まない）。
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { PlotFigure } from "./Plot";
import { RoomTransitionComparison } from "./RoomTransitionComparison";
import { api } from "../lib/api";
import { colorForYoy } from "./KpiBigPanel";
import { buildMonthAxisMark } from "../lib/chartAxis";
import { fyDate } from "../lib/fiscalYear";
import { fmtRatioPct } from "../lib/format";
import type { Direction, RoomChargeCategory, RoomChargeMonthlyRow, RoomChargeRoomRow } from "@shared/api";

const CATS: RoomChargeCategory[] = ["満額", "減額", "旧料金継続", "計算対象外", "未徴収"];
// 料金(税込)ごと × 料金表(旧=緑 / 新=青のグラデーション・安いほど淡い)。
//   旧 = 改定前の全コードと改定後に旧料金のまま取った分、新 = 改定後の新料金表のコード。系列はデータにある料金から作る。
type Table = "旧" | "新";
const bandKey = (t: Table, price: number) => `${t}|${price}`;
// Tailwind の green/blue 200〜900。料金の数に合わせて等間隔に選ぶ（2色の直線補間だと中間がくすむ）。
const RAMP: Record<Table, string[]> = {
  旧: ["#bbf7d0", "#86efac", "#4ade80", "#22c55e", "#16a34a", "#15803d", "#166534", "#14532d"],
  新: ["#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8", "#1e40af", "#1e3a8a"],
};
interface Band { key: string; table: Table; price: number; color: string; dark: boolean }
function buildBands(keys: Iterable<string>): Band[] {
  const out: Band[] = [];
  for (const t of ["旧", "新"] as Table[]) {
    const prices = [...new Set([...keys].filter((k) => k.startsWith(`${t}|`)).map((k) => Number(k.split("|")[1])))].sort((a, b) => a - b);
    const ramp = RAMP[t];
    prices.forEach((p, i) => {
      const j = prices.length === 1 ? 4 : Math.round((i * (ramp.length - 1)) / (prices.length - 1));
      out.push({ key: bandKey(t, p), table: t, price: p, color: ramp[j], dark: j >= 4 });
    });
  }
  return out;
}
// 徴収率グラフの区分（計算対象外は全期間で11人日しか無いので未徴収にまとめる）
const RATE_CATS = ["満額徴収", "減額徴収", "旧料金のまま徴収", "未徴収"] as const;
const RATE_COLOR = ["#0f766e", "#5eead4", "#f59e0b", "#cbd5e1"];
const RATE_DARK = new Set<string>([RATE_CATS[0]]);
/** 棒の中の % 表示（小さすぎる区分は出さない） */
const MIN_LABEL_SHARE = 0.06;

const n0 = (v: number) => Math.round(v).toLocaleString();
const man = (v: number) => (v / 1e4).toLocaleString(undefined, { maximumFractionDigits: 0 });
const pct = (v: number) => (Number.isFinite(v) ? `${(v * 100).toFixed(0)}%` : "—");
const monthLabel = (ym: string) => `${ym.slice(0, 4)}年${Number(ym.slice(5, 7))}月`;
const fyOf = (ym: string) => (Number(ym.slice(5, 7)) <= 3 ? Number(ym.slice(0, 4)) - 1 : Number(ym.slice(0, 4)));

/** 1か月分の集計(速報の途中月は月換算前の生値) */
interface MonthAgg {
  month: string; source: string; days: number; calendarDays: number;
  pd: Record<RoomChargeCategory, number>; amount: number; reducedAmount: number;
  newRoomPd: number; newRoomUnpaid: number; /** bandKey(旧/新, 料金) → 金額 / 徴収した人日 */ bands: Record<string, number>; bandDays: Record<string, number>;
  /** KPI 用: 基準料金がある在室日・そのうち徴収した日・未徴収分(円)・減額分(円) */
  baseDays: number; basePaidDays: number; unpaidYen: number; reducedYen: number;
}
function aggregate(rows: RoomChargeMonthlyRow[], revisionMonth: string | undefined): MonthAgg[] {
  const m = new Map<string, MonthAgg>();
  for (const r of rows) {
    let a = m.get(r.month);
    if (!a) {
      a = {
        month: r.month, source: r.source, days: r.days, calendarDays: r.calendarDays,
        pd: { 満額: 0, 減額: 0, 旧料金継続: 0, 計算対象外: 0, 未徴収: 0 }, amount: 0, reducedAmount: 0,
        newRoomPd: 0, newRoomUnpaid: 0, bands: {}, bandDays: {},
        baseDays: 0, basePaidDays: 0, unpaidYen: 0, reducedYen: 0,
      };
      m.set(r.month, a);
    }
    a.pd[r.category] += r.patientDays;
    a.amount += r.amount;
    if (r.category === "減額") a.reducedAmount += r.amount;
    if (r.newRoom) {
      a.newRoomPd += r.patientDays;
      if (r.category === "未徴収" || r.category === "計算対象外") a.newRoomUnpaid += r.patientDays;
    }
    if (r.price > 0) {
      const isNew = revisionMonth != null && r.month >= revisionMonth && r.category !== "旧料金継続";
      const k = bandKey(isNew ? "新" : "旧", r.price);
      a.bands[k] = (a.bands[k] ?? 0) + r.amount;
      a.bandDays[k] = (a.bandDays[k] ?? 0) + r.patientDays;
    }
    a.baseDays += r.baseDays;
    if (r.category === "満額" || r.category === "減額" || r.category === "旧料金継続") a.basePaidDays += r.baseDays;
    if (r.category === "未徴収" || r.category === "計算対象外") a.unpaidYen += r.baseAmount;
    a.reducedYen += r.reducedYen;
  }
  return [...m.values()].sort((x, y) => x.month.localeCompare(y.month));
}

/** 比較列: 期間内の月を 月換算して平均する */
interface Period { label: string; sub?: string; months: MonthAgg[] }
function avg(p: Period, f: (a: MonthAgg) => number) {
  if (p.months.length === 0) return NaN;
  return p.months.reduce((s, a) => s + f(a) * (a.calendarDays / a.days), 0) / p.months.length;
}
const inRoom = (a: MonthAgg) => CATS.reduce((s, c) => s + a.pd[c], 0);
const unpaid = (a: MonthAgg) => a.pd.未徴収 + a.pd.計算対象外;
const paid = (a: MonthAgg) => a.pd.満額 + a.pd.減額 + a.pd.旧料金継続;

export function RoomChargePanel() {
  const { data, isLoading, error } = useQuery({ queryKey: ["room_charge"], queryFn: api.roomCharge, staleTime: 5 * 60_000 });
  const [comparison, setComparison] = useState<{ from: string; to: string }[] | null>(null);
  const months = useMemo(() => aggregate(data?.monthly ?? [], data?.meta?.revisionMonth), [data]);

  if (isLoading) return <div className="card p-8 animate-pulse text-slate-400">個室料を読み込み中…</div>;
  if (error) {
    return (
      <div className="card border-red-200 bg-red-50 p-6">
        <h2 className="font-semibold text-red-800">個室料を取得できません</h2>
        <p className="mt-1 text-sm text-red-700">{(error as Error).message}</p>
      </div>
    );
  }
  if (!data?.meta || months.length === 0) {
    return (
      <div className="card p-8">
        <h2 className="text-lg font-semibold text-slate-800">個室料は準備中です</h2>
        <p className="mt-2 text-sm text-slate-600">
          電子カルテからの収集（collect_room_charge.sh）とビルド（build_room_charge.R）がまだ走っていません。
        </p>
      </div>
    );
  }
  const meta = data.meta;
  const rev = meta.revisionMonth;

  // 比較列（自動）: 最新年度より前の年度は月平均で1列、最新年度は月ごとに1列（月が進むと列が増える）
  const revFy = fyOf(`${rev}-01`);
  const latestFy = fyOf(`${months[months.length - 1].month}-01`);
  const periods: Period[] = [];
  const fys = [...new Set(months.map((a) => fyOf(`${a.month}-01`)))].sort();
  for (const fy of fys) {
    const ms = months.filter((a) => fyOf(`${a.month}-01`) === fy);
    if (fy < latestFy) {
      periods.push({ label: `${fy}年度`, sub: ms.length === 12 ? "月平均" : `${ms.length}か月の月平均`, months: ms });
    } else {
      for (const a of ms) {
        periods.push({
          label: monthLabel(a.month),
          sub: a.month < rev ? "個室料改定前" : a.month === rev ? "個室料改定月" : "個室料改定後",
          months: [a],
        });
      }
    }
  }
  // 料金ごとの比較で使う 改定年度の個室料改定前の月
  const preRev = months.filter((a) => fyOf(`${a.month}-01`) === revFy && a.month < rev);
  // 徴収率の分母 = その期間に個室(基準料金あり)だった部屋の在室日。改定で個室にした部屋は改定前は入らない。
  const collect = (p: Period) => avg(p, (a) => a.basePaidDays) / avg(p, (a) => a.baseDays);

  /** [ラベル, 値, 段(0=見出し / 1=うち / 2=内数)] */
  const T: [string, (p: Period) => string, number?][] = [
    ["個室料売上（万円）", (p) => man(avg(p, (a) => a.amount))],
    ["個室料徴収率", (p) => pct(collect(p))],
    ["個室在室数（人日）", (p) => n0(avg(p, inRoom))],
    ["未徴収数", (p) => n0(avg(p, unpaid)), 1],
    ["個室料徴収数", (p) => n0(avg(p, paid)), 1],
    ["満額徴収", (p) => n0(avg(p, (a) => a.pd.満額)), 2],
    ["減額徴収", (p) => n0(avg(p, (a) => a.pd.減額)), 2],
    ["旧料金のまま徴収", (p) => (p.months.every((a) => a.month >= rev) ? n0(avg(p, (a) => a.pd.旧料金継続)) : "—"), 2],
    ["徴収1日あたり室料（円）", (p) => n0(avg(p, (a) => a.amount) / avg(p, paid))],
  ];


  // 月別グラフ(速報月は月換算)
  const scaled = (a: MonthAgg) => a.calendarDays / a.days;
  // 系列 = 実際に取った料金 ＋ 料金表(室料マスタ)の料金（取った日が無い料金も比較グラフに色を持たせる）
  const bands = buildBands(new Set([
    ...months.flatMap((a) => Object.keys(a.bands)),
    ...data.rooms.flatMap((r) => [r.oldPrice != null ? bandKey("旧", r.oldPrice) : null, r.newPrice != null ? bandKey("新", r.newPrice) : null])
      .filter((k): k is string => k != null),
  ]));
  // 積み上げ順 = 料金の安い順（旧・新をまたいで 5,000→8,800→10,000→11,000…・同額は旧が先）。凡例は料金表ごとのまま
  const bandOrder = [...bands].sort((a, b) => a.price - b.price || (a.table === b.table ? 0 : a.table === "旧" ? -1 : 1)).map((b) => b.key);
  const bandByKey = new Map(bands.map((b) => [b.key, b]));
  const bandLabel = (k: string) => { const b = bandByKey.get(k)!; return `${b.table}料金表 ${n0(b.price)}円`; };
  type AmtRow = { month: string; band: string; v: number; share: number };
  const amtRows: AmtRow[] = months.flatMap((a) => {
    const tot = bandOrder.reduce((s, b) => s + (a.bands[b] ?? 0), 0);
    return bandOrder.map((b) => ({ month: a.month, band: b, v: ((a.bands[b] ?? 0) * scaled(a)) / 1e4, share: tot > 0 ? (a.bands[b] ?? 0) / tot : 0 }));
  });
  type RateRow = { month: string; cat: string; days: number; share: number };
  const rateRows: RateRow[] = months.flatMap((a) => {
    const vals = [a.pd.満額, a.pd.減額, a.pd.旧料金継続, a.pd.未徴収 + a.pd.計算対象外];
    const tot = vals.reduce((s, v) => s + v, 0);
    return RATE_CATS.map((c, i) => ({ month: a.month, cat: c, days: vals[i], share: tot > 0 ? vals[i] / tot : 0 }));
  });
  const pctLabel = (v: number) => (v >= MIN_LABEL_SHARE ? `${Math.round(v * 100)}%` : "");
  const monthDomain = months.map((a) => a.month);
  // 全月に「4月」・最初の月と1月は2行目に年（4月 / 2024年）
  const monthTick = (d: string) => {
    const m = Number(d.slice(5, 7));
    return d === monthDomain[0] || m === 1 ? `${m}月\n${d.slice(0, 4)}年` : `${m}月`;
  };
  const amtTotals = months.map((a) => ({ month: a.month, v: amtRows.filter((r) => r.month === a.month).reduce((s, r) => s + r.v, 0) }));
  const amtOpts: Plot.PlotOptions = {
    width: 1600, height: 320, marginLeft: 48, marginTop: 18, marginBottom: 36,
    x: { domain: monthDomain, tickFormat: monthTick, label: null, tickSize: 0 },
    y: { grid: true, label: "万円／月" },
    color: { domain: bandOrder, range: bandOrder.map((k) => bandByKey.get(k)!.color) },
    marks: [
      Plot.ruleX([rev], { stroke: "#b45309", strokeDasharray: "3,3" }),
      Plot.barY(amtRows, { x: "month", y: "v", fill: "band", order: bandOrder, title: (d: AmtRow) => `${monthLabel(d.month)} ${bandLabel(d.band)} ${n0(d.v)}万円（${pct(d.share)}）` }),
      Plot.text(amtRows, Plot.stackY({ x: "month", y: "v", z: "band", order: bandOrder, text: (d: AmtRow) => pctLabel(d.share),
        fill: (d: AmtRow) => (bandByKey.get(d.band)?.dark ? "white" : "#0f172a"), fontSize: 9 })),
      // 棒の上に月の合計（万円）
      Plot.text(amtTotals, { x: "month", y: "v", text: (d: { v: number }) => n0(d.v), dy: -7, fontSize: 10, fill: "#334155" }),
      Plot.ruleY([0]),
    ],
  };
  const pdOpts: Plot.PlotOptions = {
    width: 1600, height: 300, marginLeft: 48, marginBottom: 36,
    x: { domain: monthDomain, tickFormat: monthTick, label: null, tickSize: 0 },
    y: { grid: true, label: "個室の在室日に占める割合", domain: [0, 1], tickFormat: (v: number) => `${Math.round(v * 100)}%` },
    color: { domain: [...RATE_CATS], range: RATE_COLOR },
    marks: [
      Plot.ruleX([rev], { stroke: "#b45309", strokeDasharray: "3,3" }),
      Plot.barY(rateRows, { x: "month", y: "share", fill: "cat", order: [...RATE_CATS], title: (d: RateRow) => `${monthLabel(d.month)} ${d.cat} ${n0(d.days)}人日（${pct(d.share)}）` }),
      Plot.text(rateRows, Plot.stackY({ x: "month", y: "share", z: "cat", order: [...RATE_CATS], text: (d: RateRow) => pctLabel(d.share),
        fill: (d: RateRow) => (RATE_DARK.has(d.cat) ? "white" : "#0f172a"), fontSize: 9 })),
      Plot.ruleY([0]),
    ],
  };

  // 部屋の新旧対応(室料マスタの区分01)
  const groups = new Map<string, { oldPrice: number | null; newPrice: number | null; exTax: number | null; rooms: string[] }>();
  for (const r of data.rooms) {
    const k = `${r.oldPrice ?? "-"}>${r.newPrice ?? "-"}`;
    const g = groups.get(k) ?? { oldPrice: r.oldPrice, newPrice: r.newPrice, exTax: r.newPriceExTax, rooms: [] };
    g.rooms.push(r.label ?? r.room);
    groups.set(k, g);
  }
  // ── 料金ごとの比較（改定前 ⇔ 改定後）: 部屋数 / 徴収した延べ人日 / 売上 の割合 ──
  type CmpRow = { metric: string; side: string; key: string; v: number; share: number };
  const postMonths = months.filter((a) => a.month >= rev);
  const defaultRange = (ms: MonthAgg[]) => ({
    from: (ms[0] ?? months[0]).month,
    to: (ms[ms.length - 1] ?? months[0]).month,
  });
  const ranges = comparison ?? [defaultRange(preRev), defaultRange(postMonths)];
  const selectedMonths = ranges.map(({ from, to }) => months.filter((a) => a.month >= from && a.month <= to));
  const sideLabel = (i: number) => `期間${i + 1}\n${ranges[i].from.replace("-", "/")}${ranges[i].to !== ranges[i].from ? `〜${ranges[i].to.replace("-", "/")}` : ""}`;
  const PRE_SIDE = sideLabel(0), POST_SIDE = sideLabel(1);
  const changeRange = (i: number, field: "from" | "to", value: string) => {
    setComparison(ranges.map((r, j) => {
      if (i !== j) return r;
      const next = { ...r, [field]: value };
      if (next.from > next.to) next[field === "from" ? "to" : "from"] = value;
      return next;
    }));
  };
  const toRows = (metric: string, side: string, vals: Map<string, number>): CmpRow[] => {
    const tot = [...vals.values()].reduce((s, v) => s + v, 0);
    return [...vals].filter(([, v]) => v > 0).map(([key, v]) => ({ metric, side, key, v, share: tot > 0 ? v / tot : 0 }));
  };
  const sumBy = (ms: MonthAgg[], f: (a: MonthAgg) => Record<string, number>) => {
    const m = new Map<string, number>();
    for (const a of ms) for (const [k, v] of Object.entries(f(a))) m.set(k, (m.get(k) ?? 0) + (v * scaled(a)) / ms.length);
    return m;
  };
  // 室料の無い部屋（改定前の 4D 6室など）は数えない
  const roomCount = (price: (r: RoomChargeRoomRow) => number | null, t: Table) => {
    const m = new Map<string, number>();
    for (const r of data.rooms) {
      const p = price(r);
      if (p != null) m.set(bandKey(t, p), (m.get(bandKey(t, p)) ?? 0) + 1);
    }
    return m;
  };
  const CMP_METRICS = ["部屋数", "徴収した延べ人日（月平均）", "売上（月平均）"] as const;
  const cmpRows: CmpRow[] = [
    ...toRows(CMP_METRICS[0], "旧料金表（個室料改定前日）", roomCount((r) => r.oldPrice, "旧")),
    ...toRows(CMP_METRICS[0], "新料金表（個室料改定日）", roomCount((r) => r.newPrice, "新")),
    ...toRows(CMP_METRICS[1], PRE_SIDE, sumBy(selectedMonths[0], (a) => a.bandDays)),
    ...toRows(CMP_METRICS[1], POST_SIDE, sumBy(selectedMonths[1], (a) => a.bandDays)),
    ...toRows(CMP_METRICS[2], PRE_SIDE, sumBy(selectedMonths[0], (a) => a.bands)),
    ...toRows(CMP_METRICS[2], POST_SIDE, sumBy(selectedMonths[1], (a) => a.bands)),
  ];
  const keyOrder = bandOrder;
  const keyColor = new Map<string, string>(bands.map((b) => [b.key, b.color] as [string, string]));
  const keyName = (k: string) => `${n0(bandByKey.get(k)!.price)}円`;
  const unitOf = (metric: string) => (metric === CMP_METRICS[0] ? "部屋" : metric === CMP_METRICS[1] ? "人日" : "万円");
  const fmtV = (metric: string, v: number) => (metric === CMP_METRICS[2] ? n0(v / 1e4) : n0(v));
  /** 棒の中の文字: 部屋数は「5,000円 17部屋（23%）」、ほかは「5,000円 44%」。狭い区分は短くする */
  /** 表示用の実数（売上は万円） */
  const dispV = (metric: string, v: number) => (metric === CMP_METRICS[2] ? v / 1e4 : v);
  /** 棒の中の文字（2行）: 「5,000円 / 17部屋（25%）」。棒が短い区分は割合を落とす・さらに短ければ数だけ・出さない */
  const cmpLabel = (metric: string, d: CmpRow, frac: number) => {
    const num = `${n0(dispV(metric, d.v))}${unitOf(metric)}`;
    const p = `${Math.round(d.share * 100)}%`;
    return frac >= 0.075 ? `${keyName(d.key)}\n${num}（${p}）` : frac >= 0.04 ? `${keyName(d.key)}\n${num}` : frac >= 0.025 ? num : "";
  };
  const cmpOpts = (metric: string): Plot.PlotOptions => {
    const rows = cmpRows.filter((r) => r.metric === metric).map((r) => ({ ...r, dv: dispV(metric, r.v) }));
    const sides = metric === CMP_METRICS[0]
      ? ["旧料金表（個室料改定前日）", "新料金表（個室料改定日）"] : [PRE_SIDE, POST_SIDE];
    const maxTot = Math.max(...sides.map((sd) => rows.filter((r) => r.side === sd).reduce((s, r) => s + r.dv, 0)), 1);
    type R = (typeof rows)[number];
    return {
      width: 1600, height: 150, marginLeft: 180, marginRight: 60, marginTop: 4, marginBottom: 22,
      x: { grid: true, label: null, domain: [0, maxTot], nice: true, tickFormat: (v: number) => v.toLocaleString() },
      y: { label: null, domain: sides, tickSize: 0 },
      color: { domain: keyOrder, range: keyOrder.map((k) => keyColor.get(k)!) },
      marks: [
        Plot.barX(rows, Plot.stackX({ y: "side", x: "dv", fill: "key", order: keyOrder, inset: 0.5,
          title: (d: R) => `${d.side} ${keyName(d.key)}: ${fmtV(metric, d.v)}${unitOf(metric)}（${pct(d.share)}）` })),
        Plot.text(rows, Plot.stackX({ y: "side", x: "dv", z: "key", order: keyOrder, fontSize: 10, lineHeight: 1.25,
          text: (d: R) => cmpLabel(metric, d, d.dv / maxTot),
          fill: (d: R) => (bandByKey.get(d.key)?.dark ? "white" : "#0f172a") })),
        // 棒の右端に合計
        Plot.text(sides.map((sd) => ({ side: sd, tot: rows.filter((r) => r.side === sd).reduce((s, r) => s + r.dv, 0) })), {
          y: "side", x: "tot", text: (d: { tot: number }) => `計 ${n0(d.tot)}${unitOf(metric)}`, textAnchor: "start", dx: 4, fontSize: 10, fill: "#334155" }),
      ],
    };
  };

  const crosswalk = [...groups.values()].sort((a, b) => (a.oldPrice ?? 0) - (b.oldPrice ?? 0) || (a.newPrice ?? 0) - (b.newPrice ?? 0));
  const change = (o: number | null, n: number | null) => {
    if (o == null) return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">新たに室料</span>;
    if (n == null) return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">個室料改定後の新料金なし</span>;
    const d = n - o;
    const cls = d > 0 ? "bg-orange-50 text-orange-700" : d < 0 ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-600";
    return <span className={`rounded-full px-2 py-0.5 text-[11px] tabular-nums ${cls}`}>{d === 0 ? "据え置き" : `${d > 0 ? "+" : ""}${n0(d)}円（${d > 0 ? "+" : ""}${Math.round((d / o) * 100)}%）`}</span>;
  };
  const yen = (v: number | null) => (v == null ? "—" : n0(v));

  return (
    <div className="space-y-3">
      <section className="card p-4">
        <h2 className="text-xl font-bold text-slate-900">個室料</h2>
      </section>

      <RoomChargeKpis months={months} revisionMonth={rev} />

      <section className="card overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-2">
          <h2 className="font-semibold text-slate-900">個室の使われ方</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] text-slate-500">
                <th className="px-3 py-1.5 text-left font-medium" />
                {periods.map((p) => (
                  <th key={p.label} className="px-3 py-1.5 text-right font-medium whitespace-nowrap">
                    <div className="text-slate-700">{p.label}</div>
                    <div>{p.sub}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {T.map(([label, f, depth = 0]) => (
                <tr key={label} className="border-b border-slate-100">
                  <td className={`py-1.5 pr-3 whitespace-nowrap ${depth === 2 ? "pl-14 text-slate-600" : depth === 1 ? "pl-8 text-slate-700" : "pl-3 text-slate-700"}`}>
                    {depth > 0 && <span className="mr-1 text-[11px] text-slate-400">{depth === 1 ? "うち" : "内数"}</span>}{label}
                  </td>
                  {periods.map((p) => <td key={p.label} className="px-3 py-1.5 text-right tabular-nums">{f(p)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid gap-3">
        <section className="card p-4">
          <h2 className="font-semibold text-slate-900">月別の個室料（料金別）</h2>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
            {(["旧", "新"] as const).map((g) => (
              <span key={g} className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-600">{g}料金表</span>
                {bands.filter((b) => b.table === g).map((b) => (
                  <span key={b.key} className="flex items-center gap-1 tabular-nums"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: b.color }} />{n0(b.price)}円</span>
                ))}
              </span>
            ))}
            <span>点線 = 個室料改定月</span>
          </div>
          <PlotFigure options={amtOpts} responsiveWidth />
          <p className="text-[11px] text-slate-500">
            料金は税込（1日あたり）。棒の中の % = その月の個室料に占める割合。旧料金表 = 個室料改定前のコードと、個室料改定後に旧料金のまま取った分。
          </p>
        </section>
        <section className="card p-4">
          <h2 className="font-semibold text-slate-900">徴収率</h2>
          <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-slate-500">
            {RATE_CATS.map((c, i) => <span key={c} className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: RATE_COLOR[i] }} />{c}</span>)}
            <span>点線 = 個室料改定月</span>
          </div>
          <PlotFigure options={pdOpts} responsiveWidth />
          <p className="text-[11px] text-slate-500">個室の在室日のうち、どう取ったか（取らなかったか）の割合。未徴収には「入力したが請求しない室料」（全期間で11人日）を含む。</p>
        </section>
      </div>

      <section className="card p-4">
        <h2 className="font-semibold text-slate-900">料金ごとの比較</h2>
        <div className="mt-3 flex flex-wrap items-end gap-4">
          {ranges.map((range, i) => (
            <fieldset key={i} className="rounded-lg border border-slate-200 px-3 py-2">
              <legend className="px-1 text-sm font-medium text-slate-700">期間{i + 1}</legend>
              <div className="flex items-center gap-2 text-sm">
                {(["from", "to"] as const).map((field) => (
                  <label key={field} className="flex items-center gap-1">
                    <span className="text-slate-500">{field === "from" ? "開始" : "終了"}</span>
                    <select aria-label={`期間${i + 1}の${field === "from" ? "開始" : "終了"}年月`}
                      value={range[field]} onChange={(e) => changeRange(i, field, e.target.value)}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-slate-800">
                      {months.map((a) => <option key={a.month} value={a.month}>{a.month.replace("-", "年")}月</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-500">収載 {selectedMonths[i].length}か月の月平均</p>
            </fieldset>
          ))}
          <button type="button" onClick={() => setComparison(null)} className="rounded border border-slate-300 px-3 py-2 text-xs text-slate-600">改定前後に戻す</button>
        </div>
        <p className="mt-2 text-xs text-slate-500">開始と終了を同じ年月にすると単月同士で比較できます。収載されている月を対象に集計します。</p>
        <RoomTransitionComparison rows={data.transitions ?? []} ranges={ranges} months={months.map(a => a.month)} />
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
          {(["旧", "新"] as const).map((g) => (
            <span key={g} className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-slate-600">{g}料金表</span>
              {bands.filter((b) => b.table === g).map((b) => (
                <span key={b.key} className="flex items-center gap-1 tabular-nums"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: b.color }} />{n0(b.price)}円</span>
              ))}
            </span>
          ))}
        </div>
        <div className="mt-2 space-y-2">
          {CMP_METRICS.slice(1).map((m) => (
            <div key={m}>
              <div className="text-[12px] font-medium text-slate-700">{m}</div>
              <PlotFigure options={cmpOpts(m)} responsiveWidth />
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">
          延べ人日・売上 = 選択した期間に実際に取った料金ごと
          （個室料改定後にも、個室料改定前からの入院で旧料金のまま取った分が緑で残る）。棒の中の % はその棒の合計に占める割合。
        </p>
      </section>

      <section className="card p-4">
        <h2 className="font-semibold text-slate-900">部屋数（料金改定前・後）</h2>
        <PlotFigure options={cmpOpts(CMP_METRICS[0])} responsiveWidth />
        <p className="text-[11px] text-slate-500">室料マスタの改定前日・改定日の料金で比較。上の期間選択には連動しません。室料の無い部屋は数えません。</p>
      </section>

      <section className="card overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-2">
          <h2 className="font-semibold text-slate-900">料金表の新旧対応（部屋ごと・税込）</h2>
          <p className="text-[11px] text-slate-500">
            電子カルテの室料マスタで部屋ごとに決まっている料金（既定の区分）。旧料金 = 個室料改定前日、新料金 = 個室料改定日時点。
            新料金表は税別で登録されているので、税込にして税別の額を添えた。
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] text-slate-500">
                <th className="px-3 py-1.5 text-left font-medium">旧料金</th>
                <th className="px-3 py-1.5 text-left font-medium">新料金</th>
                <th className="px-3 py-1.5 text-right font-medium">部屋数</th>
                <th className="px-3 py-1.5 text-left font-medium">変化</th>
                <th className="px-3 py-1.5 text-left font-medium">部屋</th>
              </tr>
            </thead>
            <tbody>
              {crosswalk.map((g) => (
                <tr key={`${g.oldPrice}>${g.newPrice}`} className="border-b border-slate-100 align-top">
                  <td className="px-3 py-1.5 tabular-nums">{g.oldPrice == null ? "（室料なし）" : yen(g.oldPrice)}</td>
                  <td className="px-3 py-1.5 tabular-nums">{g.newPrice == null ? "（室料なし）" : yen(g.newPrice)}
                    {g.exTax != null && <span className="ml-1 text-[11px] text-slate-500">（税別 {n0(g.exTax)}）</span>}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{g.rooms.length}</td>
                  <td className="px-3 py-1.5">{change(g.oldPrice, g.newPrice)}</td>
                  <td className="px-3 py-1.5 text-slate-600">{g.rooms.join("、")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
}

// ── KPI カード（入院実績タブのカードと同じ見た目・予算は無いので 前年同期間比 と年度推移だけ） ──
type KpiKey = "sales" | "payers" | "perDay" | "loss";
type KpiUnit = "万円" | "人日" | "円";
const KPI_SPECS: { key: KpiKey; label: string; unit: KpiUnit; direction: Direction; note: string }[] = [
  { key: "sales", label: "個室料売上", unit: "万円", direction: "higher_is_better", note: "税込・月平均" },
  { key: "payers", label: "個室料徴収数", unit: "人日", direction: "higher_is_better", note: "室料を取った延べ人日・月平均" },
  { key: "perDay", label: "徴収1日あたり室料", unit: "円", direction: "higher_is_better", note: "税込・室料 ÷ 徴収した人日" },
  { key: "loss", label: "未徴収・減額分", unit: "万円", direction: "lower_is_better", note: "取らなかった日と安く取った日の 基準料金との差・月平均" },
];
const scaleOf = (a: MonthAgg) => a.calendarDays / a.days;
const paidDays = (a: MonthAgg) => a.pd.満額 + a.pd.減額 + a.pd.旧料金継続;
function kpiValue(key: KpiKey, ms: MonthAgg[]): number | null {
  if (ms.length === 0) return null;
  const sum = (f: (a: MonthAgg) => number) => ms.reduce((s, a) => s + f(a), 0);
  if (key === "payers") return sum((a) => paidDays(a) * scaleOf(a)) / ms.length;
  if (key === "perDay") { const p = sum(paidDays); return p > 0 ? sum((a) => a.amount) / p : null; }
  const f = key === "sales" ? (a: MonthAgg) => a.amount : (a: MonthAgg) => a.unpaidYen + a.reducedYen;
  return sum((a) => f(a) * scaleOf(a)) / ms.length;
}
/** 徴収率 = 基準料金がある在室日のうち徴収した日（期間計で加重） */
function collectRate(ms: MonthAgg[]): number | null {
  const b = ms.reduce((s, a) => s + a.baseDays, 0);
  return b > 0 ? ms.reduce((s, a) => s + a.basePaidDays, 0) / b : null;
}
const fmtKpi = (v: number | null, unit: KpiUnit) =>
  v == null ? { value: "—", unit: "" }
    : unit === "人日" ? { value: Math.round(v).toLocaleString(), unit: "人日" }
    : unit === "円" ? { value: Math.round(v).toLocaleString(), unit: "円" }
    : { value: (v / 1e4).toLocaleString(undefined, { maximumFractionDigits: 0 }), unit: "万円" };
const pct1 = (v: number | null) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const shiftYear = (ym: string, d: number) => `${Number(ym.slice(0, 4)) + d}${ym.slice(4)}`;

function RoomChargeKpis({ months, revisionMonth }: { months: MonthAgg[]; revisionMonth: string }) {
  const latest = months[months.length - 1].month;
  const fy = fyOf(`${latest}-01`);
  const fyMonths = months.filter((a) => fyOf(`${a.month}-01`) === fy).map((a) => a.month);
  const defFrom = revisionMonth >= fyMonths[0] && revisionMonth <= latest ? revisionMonth : fyMonths[0];
  const [from, setFrom] = useState(defFrom);
  const [to, setTo] = useState(latest);
  const byMonth = new Map(months.map((a) => [a.month, a]));
  const cur = fyMonths.filter((m) => m >= from && m <= to).map((m) => byMonth.get(m)!).filter(Boolean);
  const prev = cur.map((a) => byMonth.get(shiftYear(a.month, -1))).filter((a): a is MonthAgg => a != null);
  const hasSokuho = cur.some((a) => a.source === "速報");
  const xDomain: [Date, Date] = [new Date(fyDate(fy, 4)), new Date(fyDate(fy, 3))];
  const sel = "border border-slate-300 rounded px-2 py-1 bg-white text-sm";
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="text-xl font-bold">個室料（{fy}年度 {Number(from.slice(5))}月〜{Number(to.slice(5))}月の月平均）</h2>
        <label className="text-xs text-slate-600 flex items-center gap-1">開始月
          <select id="room-kpi-from" className={sel} value={from} onChange={(e) => { setFrom(e.target.value); if (e.target.value > to) setTo(e.target.value); }}>
            {fyMonths.map((m) => <option key={m} value={m}>{Number(m.slice(5))}月</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-600 flex items-center gap-1">終了月
          <select id="room-kpi-to" className={sel} value={to} onChange={(e) => { setTo(e.target.value); if (e.target.value < from) setFrom(e.target.value); }}>
            {fyMonths.map((m) => <option key={m} value={m}>{Number(m.slice(5))}月</option>)}
          </select>
        </label>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span style={{ width: 16, height: 3, background: "#1B9E77", display: "inline-block" }} />今年度実績</span>
          <span className="flex items-center gap-1"><span style={{ width: 16, height: 0, borderTop: "2px solid #94a3b8", opacity: 0.7, display: "inline-block" }} />前年度実績</span>
          <span className="flex items-center gap-1"><span style={{ width: 16, height: 0, borderTop: "2px dashed #b45309", display: "inline-block" }} />個室料改定月</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 print-4cols">
        {KPI_SPECS.map((spec) => (
          <RoomKpiCard key={spec.key} spec={spec} cur={kpiValue(spec.key, cur)} py={kpiValue(spec.key, prev)}
            rate={spec.key === "payers" ? { cur: collectRate(cur), py: collectRate(prev) } : null}
            months={months} fy={fy} xDomain={xDomain} revisionMonth={revisionMonth} />
        ))}
      </div>
      <p className="text-[11px] text-slate-500">
        前年比は前年同期間（同じ月）との比較。{hasSokuho && "速報の月は集計日数から1か月に換算。"}
        基準料金 = 電子カルテの室料マスタで部屋ごとに決まっている本来の料金（個室料改定前からの入院で旧料金のまま取った日は旧料金）。
        個室料改定で個室にした部屋は個室料改定前は対象外。徴収率 = 基準料金がある在室日のうち室料を取った日。
        未徴収・減額分 = 取らなかった日の基準料金 ＋ 基準料金より安く取った差額。
      </p>
    </section>
  );
}

function RoomKpiCard({ spec, cur, py, rate, months, fy, xDomain, revisionMonth }: {
  spec: (typeof KPI_SPECS)[number]; cur: number | null; py: number | null;
  rate: { cur: number | null; py: number | null } | null;
  months: MonthAgg[]; fy: number; xDomain: [Date, Date]; revisionMonth: string;
}) {
  const main = fmtKpi(cur, spec.unit);
  const pyTxt = fmtKpi(py, spec.unit);
  const yoy = fmtRatioPct(cur, py);
  type Row = { date: Date; value: number; series: "今年度" | "前年度" };
  const rows: Row[] = [];
  for (const a of months) {
    const f = fyOf(`${a.month}-01`);
    if (f !== fy && f !== fy - 1) continue;
    const v = kpiValue(spec.key, [a]);
    if (v == null) continue;
    const ym = f === fy ? a.month : shiftYear(a.month, 1);
    rows.push({ date: new Date(`${ym}-01`), value: spec.unit === "万円" ? v / 1e4 : v, series: f === fy ? "今年度" : "前年度" });
  }
  const revDate = new Date(`${revisionMonth}-01`);
  const options: Plot.PlotOptions = {
    width: 520, height: 116, marginLeft: 42, marginRight: 6, marginTop: 4, marginBottom: 24,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "8px" },
    x: { label: null, type: "time", axis: null, domain: xDomain },
    y: { label: null, grid: true, nice: true, zero: false,
         tickFormat: (v: number) => Math.round(v).toLocaleString() },
    marks: [
      Plot.lineY(rows.filter((d) => d.series === "前年度"), { x: "date", y: "value", stroke: "#94a3b8", strokeWidth: 1.5, strokeOpacity: 0.65 }),
      Plot.lineY(rows.filter((d) => d.series === "今年度"), { x: "date", y: "value", stroke: "#1B9E77", strokeWidth: 2.4 }),
      Plot.dot(rows, { x: "date", y: "value", fill: (d: Row) => (d.series === "今年度" ? "#1B9E77" : "#94a3b8"), r: 1.8 }),
      ...(revDate >= xDomain[0] && revDate <= xDomain[1] ? [Plot.ruleX([revDate], { stroke: "#b45309", strokeDasharray: "3,3" })] : []),
      Plot.tip(rows, Plot.pointerX({ x: "date", y: "value", fontSize: 11,
        title: (d: Row) => `${d.series}: ${Math.round(d.value).toLocaleString()}${spec.unit}` })),
      buildMonthAxisMark(xDomain),
    ],
  };
  return (
    <div className="card relative border-l-4 border-l-brand p-3 min-w-0 inpatient-compare-card">
      <div className="text-base font-bold text-slate-700 leading-tight mb-1 compare-card-title">{spec.label}</div>
      <div className="text-[12px] leading-tight text-left compare-card-body">
        <div className="flex items-baseline gap-x-2 flex-wrap">
          <span className="text-2xl font-extrabold tabular-nums text-slate-900 compare-card-main">
            {main.value}{main.value !== "—" && main.unit && <span className="text-sm font-bold text-slate-400 ml-1">{main.unit}</span>}
          </span>
          <span className={`font-bold tabular-nums ${colorForYoy(cur, py, spec.direction)}`}>前年比 {yoy}</span>
        </div>
        <div className="mt-0.5 flex items-baseline gap-x-3 flex-wrap text-slate-500">
          <span>前年度 <span className="tabular-nums text-slate-600">{pyTxt.value}{pyTxt.value !== "—" && pyTxt.unit}</span></span>
          {rate && (
            <span className="text-slate-700">徴収率 <b className={`tabular-nums ${colorForYoy(rate.cur, rate.py, "higher_is_better")}`}>{pct1(rate.cur)}</b>
              <span className="text-slate-500">（前年度 {pct1(rate.py)}）</span></span>
          )}
          <span className="text-[11px]">{spec.note}</span>
        </div>
      </div>
      <div className="mt-2"><PlotFigure options={options} responsiveWidth /></div>
    </div>
  );
}
